import * as classnames from 'classnames';
import * as React from 'react';
import styled from 'styled-components';
import { CheckboxIcon, EmptyBoxIcon } from '@estee/elc-icons';
import { Body2 } from '@estee/elc-typography';
import { checkboxTheme } from '../theme/default-theme';
import { observer } from 'mobx-react';

export interface ICheckBox {
    enabled?: boolean;
    isChecked?: boolean;
    isLeftPosition?: boolean;
    label: string;
    value: string;
    className?: string;
    dataTestId?: string;
    labelId?: string;

    onChange?(isChecked?: boolean, value?: string): void;

    'aria-label'?: string;
    'aria-describedby'?: string;
    'aria-labelledby'?: string;
}

const CheckboxWrapper = styled.div`
    padding: 0;
    user-select: none;
    ${checkboxTheme};
`;
const CheckArea = styled.div`
    display: inline-flex;
    cursor: pointer;
    position: relative;
    top: 6px;
`;

const CheckLabel = styled(Body2)`
    margin-left: 5px;
    display: inline;

    [dir='rtl'] & {
        margin-left: 0;
        margin-right: 5px;
    }
`;

@observer
class CheckBox extends React.Component<ICheckBox> {
    private readonly randomLabelId: string;

    constructor(props: ICheckBox) {
        super(props);

        const randomId = btoa(`${performance.now()}_${Math.random()}`).replace(/=+/, '');
        this.randomLabelId = `${randomId}_checkbox_label`;
    }

    public render() {
        const {
            props: {
                isChecked,
                enabled,
                labelId,
                label,
                dataTestId,
                'aria-label': ariaLabel,
                'aria-describedby': ariaDescribedBy,
                'aria-labelledby': ariaLabeledBy
            }
        } = this;

        const checkClass = classnames({
            'elc-checkbox-check-area': true,
            'js-checkbox-check-area': true,
            'elc-checkbox-selected': isChecked,
            'elc-checkbox-not-selected': !isChecked
        });

        const wrapperClass = classnames({
            'elc-checkbox': true,
            'js-checkbox': true,
            'elc-checkbox-is-disabled': enabled === false
        });

        const checkBoxLabel = classnames('elc-checkbox-label', 'js-checkbox-label');

        const targetLabelId = labelId || this.randomLabelId;

        return (
            <CheckboxWrapper className={wrapperClass}>
                <CheckArea
                    className={checkClass}
                    data-test-id={dataTestId}
                    onKeyPress={this.handleKeyPress}
                    onClick={this.checkClick}
                    role="checkbox"
                    aria-checked={!!isChecked}
                    aria-labelledby={ariaLabeledBy || targetLabelId}
                    aria-label={ariaLabel}
                    aria-describedby={ariaDescribedBy}
                    aria-disabled={!enabled}
                    tabIndex={0}
                >
                    {isChecked ? (
                        <CheckboxIcon className="elc-checkbox-icon js-checkbox-icon" />
                    ) : (
                        <EmptyBoxIcon className="elc-checkbox-icon js-checkbox-icon" />
                    )}
                </CheckArea>
                <CheckLabel
                    id={targetLabelId}
                    dangerouslySetInnerHTML={{ __html: label }}
                    className={checkBoxLabel}
                />
            </CheckboxWrapper>
        );
    }

    private checkClick = (e: React.SyntheticEvent<HTMLDivElement>) => {
        const srcElement = e.nativeEvent.srcElement as Element;
        const { props } = this;

        if (props.enabled === false || srcElement instanceof HTMLAnchorElement) {
            return;
        }

        if (props.onChange) {
            props.onChange(!Boolean(props.isChecked), props.value);
        }
    };
    private handleKeyPress = (e: React.KeyboardEvent<HTMLDivElement>) => {
        const spaceKeyCode = 32;
        if (e.charCode === spaceKeyCode) {
            e.preventDefault();
            this.checkClick(e);
        }
    };
}

export { CheckBox };
